import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2, AlertCircle, Youtube, Sparkles, BarChart3 } from "lucide-react";

export default function Login() {
  const { user, login, register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-card border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 grid-bg radial-fade opacity-40 pointer-events-none" />
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground neon-primary">
            <Zap className="h-5 w-5" />
          </span>
          <span className="font-display text-xl font-bold tracking-tight">
            Video<span className="gradient-text">Lens</span>
          </span>
        </div>
        <div className="relative space-y-6 max-w-md">
          <h1 className="font-display text-4xl font-black tracking-tight leading-tight">
            Decode any YouTube video with <span className="gradient-text">AI</span>.
          </h1>
          <p className="text-muted-foreground">
            Paste a YouTube video or Short and get an instant creative teardown — hook strength,
            pacing, audio design, scroll-stop probability and a full 13-dimension performance score.
          </p>
          <div className="space-y-3 pt-2">
            {[
              { icon: Youtube, t: "Real YouTube metadata & engagement stats" },
              { icon: Sparkles, t: "AI breakdown across 13 creative dimensions" },
              { icon: BarChart3, t: "Compare, benchmark & track every teardown" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-3 text-sm">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <f.icon className="h-4 w-4" />
                </span>
                {f.t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative flex items-center gap-4 text-[11px] text-muted-foreground">
          <span>© 2026 VideoLens</span>
          <span aria-hidden>·</span>
          <Link to="/terms" data-testid="login-terms-link" className="hover:text-foreground transition-colors">
            Terms &amp; Conditions
          </Link>
          <span aria-hidden>·</span>
          <Link to="/privacy" data-testid="login-privacy-link" className="hover:text-foreground transition-colors">
            Privacy Policy
          </Link>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Zap className="h-4 w-4" />
            </span>
            <span className="font-display text-lg font-bold tracking-tight">
              Video<span className="gradient-text">Lens</span>
            </span>
          </div>

          <h2 className="font-display text-2xl font-bold tracking-tight">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            {mode === "login" ? "Sign in to your workspace." : "Start analyzing YouTube videos in seconds."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input id="name" value={form.name} onChange={set("name")} required placeholder="Jane Marketer" data-testid="auth-name" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required placeholder="you@company.com" data-testid="auth-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" data-testid="auth-password" />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-destructive" data-testid="auth-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" className="w-full h-11" disabled={busy} data-testid="auth-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-6">
            {mode === "login" ? "New to VideoLens?" : "Already have an account?"}{" "}
            <button
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary font-medium hover:underline"
              data-testid="auth-toggle"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          {/* Mobile-only footer (left panel is hidden on small screens) */}
          <div className="lg:hidden mt-8 flex items-center justify-center gap-3 text-[11px] text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground transition-colors">
              Terms
            </Link>
            <span aria-hidden>·</span>
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
